import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import SvgSearch from '@material-ui/icons/Place';

import leaflet from 'leaflet';

import {helper, http, api} from '../common/common';
import ViewStack from './ViewStack';

const styles = (theme) => {
    return {
        paper: {
            backgroundColor: theme.palette.primary.main,
            width: '100%',
            height: '100%',
        },
        select: {
            marginTop: 0,
            width: '100%',
            textAlign: 'left',
            fontSize: 13,
            color: theme.palette.primary.displayText,
        },
        selectDisplay: {
            paddingLeft: 16,
            paddingRight: 32,
            textAlign: 'center',
            '&$selectDisabled': {
                color: theme.palette.primary.disabledDisplayText,
            },
            '&:focus': {
                backgroundColor: theme.palette.common.transparent,
            }
        },
        selectMenu: {
            backgroundColor: theme.palette.primary.main,
            maxHeight: '40%',
        },
        selectMenuList: {
            margin: 0,
            padding: 0,
            borderColor: theme.palette.primary.mainBorder,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: theme.palette.primary.main,
        },
        selectMenuItem: {
            fontSize: 13,
            margin: 0,
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 16,
            color: theme.palette.primary.displayText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.hoverOverlay,
            },
            '&$selectSelected': {
            },
            '&$selectSelected:hover': {
                backgroundColor: theme.palette.primary.hoverOverlay,
            },
            '&$selectSelected:not(:hover)': {
                backgroundColor: theme.palette.primary.main,
            }
        },
        selectIcon: {
            ...theme.iconStyle.size[18],
            marginRight: 12,
            top: 'calc(50% - 9px)',
            right: 0,
            position: 'absolute',
            pointerEvents: 'none',
            color: theme.palette.primary.displayText,
            '&$selectDisabled': {
                color: theme.palette.primary.disabledDisplayText,
            }
        },
        selectDefault: {},
        selectDisabled: {},
        selectSelected: {},
        inputError: {},
        inputFocused: {},
        inputDisabled: {},
    };
}

class RegionControl extends Component {
    constructor(props) {
        super(props);

        this.state = {
            timestamp: 0,
        };

        this._cancelToken = http.CancelToken.source();
        this._locationList = [];
        this._boundsObj = leaflet.geoJson(null, {
            interactive: false,
        });
    }

    componentDidMount = () => {
        this._requestMapping();
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        return true;
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        if (!this.props.viewStack.isDefaultBase()) {
            return null;
        }

        const {classes} = this.props;

        const selectClasses = {
            select: classes.selectDisplay,
            disabled: classes.selectDisabled,
        };

        const inputClasses = {
            error: classes.inputError,
            focused: classes.inputFocused,
            disabled: classes.inputDisabled,
        };

        const menuProps = {
            classes: {
                paper: classes.selectMenu,
            },
            MenuListProps: {
                className: classes.selectMenuList,
            },
            PopoverClasses: {
            },
            getContentAnchorEl: null,
            anchorReference: 'anchorEl',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
        };

        this._constructList();

        return (
            <div className={this.props.className}>
                <Paper
                    className={classes.paper}
                    square={false}
                >
                    <Select
                        className={classes.select}
                        classes={selectClasses}
                        input={<Input classes={inputClasses} disableUnderline={true}/>}
                        MenuProps={menuProps}
                        IconComponent={this._renderDropDownArrow}
                        value={0}
                        onChange={this._handleSelection}
                        renderValue={this._renderSelectValue}
                        disabled={this._locationList.length === 0}
                    >
                        {this._locationList.map(this._renderListItem)}
                    </Select>
                </Paper>
            </div>
        );
    }

    _renderDropDownArrow = () => {
        const disabled = (this._locationList.length === 0);

        const {classes} = this.props;

        const selectState = (disabled) ? classes.selectDisabled : classes.selectDefault;

        return (
            <SvgSearch className={classNames(classes.selectIcon, selectState)}/>
        );
    }

    _renderSelectValue = (value) => {
        return (
            <div>
                Location
            </div>
        );
    }

    _renderListItem = (data, index, arr) => {
        const {classes} = this.props;

        const menuItemClasses = {
            root: classes.selectMenuItem,
            selected: classes.selectSelected,
        }

        return (
            <MenuItem
                classes={menuItemClasses}
                value={index}
                key={data.properties.key}
            >
                {data.properties.name}
            </MenuItem>
        );
    }

    _handleSelection = (event) => {
        this._boundsObj.clearLayers();
        this._boundsObj.addData(this._locationList[event.target.value]);
        const bounds = this._boundsObj.getBounds();

        if (bounds.isValid) {
            this.props.map.fitBounds(bounds);
        }

        if (this.props.onChange) {
            this.props.onChange(event.target.value);
        }
    }

    _constructList = () => {
        const view = this.props.viewStack.peek();
        const featureData = view.region.featureData;

        if (view.loaded && featureData && featureData.length &&
            this.state.timestamp && this._needMergeList) {

            const temp = {};

            for (const f of featureData) {
                const organization = f.properties.organization;

                if (!temp[organization]) {
                    temp[organization] = [];
                }

                temp[organization].push(f);
            }

            Object.entries(this._locationMap).forEach(([index, value]) => {
                const arr = (value.length) ? value : temp[index];

                if (arr) {
                    for (const f of arr) {
                        this._locationList.push(f);
                    }
                }
            });

            this._needMergeList = false;
        }

        this._locationList.sort(this._sortList);
    }

    _sortList = (left, right) => {
        return left.properties.name.localeCompare(
            right.properties.name, 'en', {sensitivity: 'base'});
    }

    _processError = (error) => {
        if (error.response && error.response.status === 404) {
            return;
        }

        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    _requestMapping = async () => {
        try {
            const response = await http.get(api.permission() + '/mapping', {
                params: {
                    'access_code': this.props.getAccessCode(),
                },
                cancelToken: this._cancelToken.token,
            });

            const regionMap = response.data.key

            this._requestRegions(regionMap);
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            const e = helper.sanitizeError(error, 'Area Error', 'Unable to load area');
            this._processError(e);
        }
    }

    _requestRegions = async (regionMap) => {
        try {
            this._locationMap = {};
            this._locationList = [];
            this._needMergeList = true;

            const response = await http.all(regionMap.map(this._requestSingleZone));

            for (const r of response) {
                this._locationMap[r.key] = r.result;
            }

            this.setState({
                timestamp: Date.now(),
            })
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }
        }
    }

    _requestSingleZone = async (organization) => {
        try {
            this._locationMap[organization] = [];

            const response = await http.get(api.map() + '/location', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': organization,
                },
                cancelToken: this._cancelToken.token,
            });

            const result = [];

            if (Object.entries(response.data.content).length) {
                for (const f of response.data.content.features) {
                    f.properties.organization = organization;
                    result.push(f);
                }
            }

            return {
                key: organization,
                result: result,
            };
        } catch (error) {
            throw error;
        }
    }
}

RegionControl.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    viewStack: PropTypes.instanceOf(ViewStack).isRequired,
    map: PropTypes.instanceOf(leaflet.Map).isRequired,
    onChange: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles)(RegionControl);
